import { CvDto } from 'modules/cv-builder/api-types';
import { CV_BUILDER_STEPS } from 'modules/cv-builder/constants';
import { PROGRESS_STEPS } from 'modules/cv-builder/stepMeta';

// The data steps (backend slugs) in wizard order — the ones that get "completed" by
// clicking Next. Same 7 steps the progress bar counts, read from the single step table so
// this list can no longer drift from it.
const DATA_STEP_SLUGS: string[] = PROGRESS_STEPS.map(step => step.slug);

// Mirrors the backend submit gate (CvService.assertSubmittable): a CV counts as complete
// when it has personal name + email, at least one education entry, and one language skill.
// Experience / skills / certificates are optional.
export const isSubmittableCv = (cv: CvDto | null | undefined): boolean =>
  !!cv &&
  !!cv.personalDetails?.firstname &&
  !!cv.personalDetails?.lastname &&
  !!cv.personalDetails?.email &&
  (cv.education?.length ?? 0) > 0 &&
  (cv.languageSkills?.length ?? 0) > 0;

/**
 * Where to drop a resuming guest. The CV is the source of truth for completeness; the
 * acknowledged step slugs only refine the resume point within an unfinished CV:
 *  - complete/submittable CV → Summary (review + per-section edit)
 *  - else empty acknowledged steps → Language (start fresh)
 *  - else → the first un-acknowledged data step
 *
 * Why the CV check comes first: `completedSteps` is stored PER-GRANT and is empty for a
 * freshly issued grant, but the CV is per-talent and persists. Without this, a talent who
 * is granted access again and already has a finished CV would be dropped back on the start
 * screen instead of their Summary. Returns a step slug suitable for `?step=`.
 */
export const computeResumeStep = (
  completedSlugs: string[],
  cv?: CvDto | null
): string => {
  if (isSubmittableCv(cv)) {
    return CV_BUILDER_STEPS.SUMMARY;
  }
  if (completedSlugs.length === 0) {
    return CV_BUILDER_STEPS.LANGUAGE;
  }
  const firstIncomplete = DATA_STEP_SLUGS.find(
    slug => !completedSlugs.includes(slug)
  );
  return firstIncomplete ?? CV_BUILDER_STEPS.SUMMARY;
};
